import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const Footer = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const isNL = language === 'nl';

  return (
    <footer className="footer" role="contentinfo" aria-label={isNL ? 'Sitevoettekst' : 'Site footer'}>
      <div className="container">
        <div className="footer-content">
          {/* Brand / Logo */}
          <div className="footer-section footer-brand">
            <div className="footer-logo">
              <img
                src={require('../assets/images/new_logo.svg').default}
                alt="MagoMeal Logo"
                style={{ height: '180px', width: 'auto', marginBottom: '24px' }}
              />
              <p className="body-medium">
                {isNL
                  ? 'Leider in de duurzame verpakkingsrevolutie met innovatieve eetbare oplossingen.'
                  : 'Leading the sustainable packaging revolution with innovative edible solutions.'}
              </p>
            </div>
          </div>

          {/* Company */}
          <div className="footer-section">
            <h4 className="heading-3">{isNL ? 'Bedrijf' : 'Company'}</h4>
            <nav className="footer-nav" aria-label={isNL ? 'Bedrijf navigatie' : 'Company navigation'}>
              <Link to="/" className="footer-link">{t('nav.home')}</Link>
              <Link to="/about" className="footer-link">{t('nav.about')}</Link>
              <Link to="/certifications" className="footer-link">{t('nav.certifications')}</Link>
              <Link to="/blog" className="footer-link">{t('nav.blog')}</Link>
              <Link to="/contact" className="footer-link">{t('nav.contact')}</Link>
              <Link to="/shop" className="footer-link">{t('nav.shop')}</Link>
            </nav>
          </div>

          {/* Product Range */}
          <div className="footer-section">
            <h4 className="heading-3">
              <Link to="/product-range" className="footer-heading-link">
                {t('nav.productRange')}
              </Link>
            </h4>
            <nav className="footer-nav" aria-label={isNL ? 'Productassortiment navigatie' : 'Product Range navigation'}>
              <Link to="/product-range" className="footer-link">
                {isNL ? 'Overzicht' : 'Overview'}
              </Link>
              <Link to="/edible-cups" className="footer-link">{t('nav.edibleCup')}</Link>
              <Link to="/cutlery-tableware" className="footer-link">{t('nav.cutleryTableware')}</Link>
            </nav>
          </div>

          {/* Use Cases */}
          <div className="footer-section">
            <h4 className="heading-3">
              <Link to="/use-cases" className="footer-heading-link">
                {t('nav.useCases')}
              </Link>
            </h4>
            <nav className="footer-nav" aria-label={isNL ? 'In de praktijk navigatie' : 'Where It’s Used navigation'}>
              <Link to="/use-cases" className="footer-link">
                {isNL ? 'Overzicht' : 'Overview'}
              </Link>
              <Link to="/use-cases/events-and-catering" className="footer-link">{t('nav.eventsCatering')}</Link>
              <Link to="/use-cases/coffee-producers" className="footer-link">{t('nav.coffeeProducers')}</Link>
              <Link to="/use-cases/hotels-restaurants-cafes" className="footer-link">{t('nav.horeca')}</Link>
              <Link to="/use-cases/corporations-companies" className="footer-link">{t('nav.corporations')}</Link>
              <Link to="/use-cases/private-gatherings" className="footer-link">{t('nav.privateGatherings')}</Link>
            </nav>
          </div>

          {/* Legal */}
          <div className="footer-section">
            <h4 className="heading-3">{isNL ? 'Juridisch' : 'Legal'}</h4>
            <nav className="footer-nav" aria-label={isNL ? 'Juridische navigatie' : 'Legal navigation'}>
              <Link to="/privacy-policy" className="footer-link">{t('footer.privacyPolicy')}</Link>
              <Link to="/terms-conditions" className="footer-link">{t('footer.termsConditions')}</Link>
              <Link to="/cookie-policy" className="footer-link">{t('footer.cookiePolicy')}</Link>
              <Link to="/delivery-return" className="footer-link">{t('footer.deliveryReturn')}</Link>
            </nav>
          </div>

          {/* Contact + Newsletter */}
          <div className="footer-section">
            <h4 className="heading-3">{isNL ? 'Neem Contact Op' : 'Get In Touch'}</h4>
            <div className="footer-contact">
              <a href="mailto:info@magomeal.nl" className="contact-item" aria-label={isNL ? 'E-mail ons' : 'Email us'}>
                <Mail size={20} />
                <span>info@magomeal.nl</span>
              </a>
              <a href="tel:+31628253218" className="contact-item" aria-label={isNL ? 'Bel ons' : 'Call us'}>
                <Phone size={20} />
                <span>+31 628 25 32 18</span>
              </a>
            </div>

            {/* Newsletter Signup */}
            <div className="newsletter-signup">
              <h5 className="body-large">{isNL ? 'Blijf Op De Hoogte' : 'Stay Updated'}</h5>
              <form className="newsletter-form" aria-label={isNL ? 'Aanmelden voor nieuwsbrief' : 'Newsletter signup'}>
                <label htmlFor="footer-newsletter-email" className="visually-hidden">
                  {t('footer.enterEmail')}
                </label>
                <input
                  id="footer-newsletter-email"
                  type="email"
                  placeholder={t('footer.enterEmail')}
                  className="newsletter-input"
                  required
                />
                <button type="submit" className="btn-primary">{t('footer.subscribeBtn')}</button>
              </form>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="body-small">
              © {new Date().getFullYear()} MagoMeal. {t('footer.allRightsReserved')}.{' '}
              {isNL
                ? 'Leveren van innovatieve eetbare producten aan de Benelux.'
                : 'Delivering innovative edible products to the Benelux.'}
            </p>
            <p className="body-small">
              Web development by Magomeal v2.7
            </p>
            <p className="body-small">
              {isNL ? 'Laatste Update August 2026.' : 'Last Update August 2026.'}
            </p>
          </div>
          <div className="footer-bottom-content">
            <p className="body-small">
              {isNL ? 'KvK nummer: 98332538' : 'Chamber of Commerce number: 98332538'}
            </p>
          </div>
          <div className="footer-bottom-content">
            <p className="body-small">
              VAT: NL005324390B18
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
